import type { GridFilterModel, GridFilterItem } from '@mui/x-data-grid-pro';
import type { SelectedFilters } from '@/components/FilterData/types';
import type { MetadataFilterValue, NumericFilter } from '@/api/generatedApi';
import { GridLogicOperator } from '@mui/x-data-grid-pro';
import { RANGE_SYMBOL_TO_API, RANGE_API_TO_SYMBOL, isNumericOperator, isRangeOperator } from './numericOperators';

function isNumericFilterObject(value: MetadataFilterValue): value is NumericFilter {
  return (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    ('gt' in value || 'gte' in value || 'lt' in value || 'lte' in value)
  );
}

export function serializeSearchFilterModel(filterModel: GridFilterModel | undefined): {
  filters: SelectedFilters;
} {
  const filters: SelectedFilters = {
    extensions: [],
    labels: [],
    folders: [],
    metadata: {},
  };

  if (!filterModel?.items || filterModel.items.length === 0) {
    return { filters };
  }

  for (const item of filterModel.items) {
    if (!isValidFilterItem(item)) continue;

    const { field, operator, value } = item;

    switch (field) {
      case 'extension': {
        const extensions = extractExtensions(operator, value);
        filters.extensions.push(...extensions);

        break;
      }
      case 'label': {
        if (operator === 'isAnyOf') {
          const labelValues = extractArrayOrStringValues(value);
          filters.labels.push(...labelValues);
        }

        break;
      }
      case 'folder': {
        const folders = extractFolders(operator, value);
        filters.folders.push(...folders);

        break;
      }
      default: {
        if (field.startsWith('metadata.')) {
          const fieldName = field.slice('metadata.'.length);
          const metadataValue = extractMetadataFilterValue(operator, value);
          if (metadataValue !== undefined) {
            const existing = filters.metadata[fieldName];
            filters.metadata[fieldName] =
              existing !== undefined && isNumericFilterObject(existing) && isNumericFilterObject(metadataValue)
                ? { ...existing, ...metadataValue }
                : metadataValue;
          }
        }
      }
    }
  }

  return { filters };
}

function isValidFilterItem(item: GridFilterItem): boolean {
  if (item.value === undefined || item.value === '') return false;
  if (!item.operator) return false;
  return true;
}

const extractArrayOrStringValues = (value: unknown, splitFunction?: (v: string) => string[]): string[] => {
  if (Array.isArray(value)) {
    return value.filter((v): v is string => typeof v === 'string');
  }
  if (typeof value === 'string') {
    return splitFunction ? splitFunction(value) : [value];
  }
  return [];
};

function splitExtensionString(value: string): string[] {
  return value
    .split(/[\s,]+/)
    .map(v => v.trim())
    .filter(v => v.length > 0);
}

function normalizeExtension(extension: string): string {
  return extension.startsWith('.') ? extension.slice(1) : extension;
}

function normalizeFolder(folder: string): string {
  const trimmed = folder.trim();
  return trimmed.endsWith('/') ? trimmed : `${trimmed}/`;
}

function denormalizeFolder(folder: string): string {
  return folder.endsWith('/') ? folder.slice(0, -1) : folder;
}

function extractExtensions(operator: string, value: unknown): string[] {
  if (operator === 'isAnyOf') {
    return extractArrayOrStringValues(value, splitExtensionString).map(v => normalizeExtension(v));
  }
  if (operator === 'equals' && typeof value === 'string') {
    return [normalizeExtension(value)];
  }
  return [];
}

function extractFolders(operator: string, value: unknown): string[] {
  if (operator === 'isAnyOf') {
    return extractArrayOrStringValues(value)
      .filter(v => v.trim() !== '')
      .map(v => normalizeFolder(v));
  }
  if (['equals', 'startsWith', 'contains'].includes(operator) && typeof value === 'string' && value.trim()) {
    return [normalizeFolder(value)];
  }
  return [];
}

function extractMetadataFilterValue(operator: string, value: unknown): MetadataFilterValue | undefined {
  if (operator === 'isAnyOf' && Array.isArray(value)) {
    const stringValues = value.filter((v): v is string => typeof v === 'string');
    if (stringValues.length === 0) {
      return undefined;
    }
    return stringValues;
  }

  if (operator === 'is') {
    if (typeof value === 'boolean') {
      return value;
    }
    if (typeof value === 'string') {
      return value === 'true';
    }
    return undefined;
  }

  if (typeof value === 'number' || (typeof value === 'string' && isNumericOperator(operator))) {
    const numberValue = typeof value === 'number' ? value : Number(value);

    if (Number.isNaN(numberValue)) {
      return typeof value === 'string' ? value : undefined;
    }

    if (isRangeOperator(operator)) {
      return { [RANGE_SYMBOL_TO_API[operator]]: numberValue } as NumericFilter;
    }

    if (operator === '=' || operator === 'equals') {
      return numberValue;
    }
  }

  if (typeof value === 'string') {
    return value;
  }

  if (typeof value === 'boolean') {
    return value;
  }

  return undefined;
}

function metadataFilterValueToOperators(value: MetadataFilterValue): Array<{ operator: string; filterValue: unknown }> {
  if (Array.isArray(value)) {
    return [{ operator: 'isAnyOf', filterValue: value }];
  }

  if (isNumericFilterObject(value)) {
    return Object.entries(value)
      .filter(([, value_]) => value_ !== undefined)
      .map(([key, value_]) => ({ operator: RANGE_API_TO_SYMBOL[key as keyof NumericFilter], filterValue: value_ }));
  }

  if (typeof value === 'boolean') {
    return [{ operator: 'is', filterValue: value }];
  }

  if (typeof value === 'string') {
    return [{ operator: 'isAnyOf', filterValue: [value] }];
  }

  return [{ operator: '=', filterValue: value }];
}

const BASE_FILTER_COLUMNS = new Set(['extension', 'folder', 'label', 'createdAt', 'creator']);

const createFilterItem = (id: number, field: string, operator: string, value: unknown): GridFilterItem => ({
  id,
  field,
  operator,
  value,
});

export function selectedFiltersToGridFilterModel(
  selectedFilters: SelectedFilters,
  availableColumns?: Set<string>
): GridFilterModel {
  const items: GridFilterItem[] = [];
  let id = 0;

  const isColumnAvailable = (field: string): boolean =>
    availableColumns ? availableColumns.has(field) : BASE_FILTER_COLUMNS.has(field);

  const addFilterIfAvailable = (field: string, operator: string, value: unknown) => {
    if (isColumnAvailable(field)) {
      items.push(createFilterItem(id++, field, operator, value));
    }
  };

  if (selectedFilters.extensions.length > 0) {
    addFilterIfAvailable('extension', 'isAnyOf', selectedFilters.extensions);
  }

  if (selectedFilters.labels?.length > 0) {
    addFilterIfAvailable('label', 'isAnyOf', selectedFilters.labels);
  }

  if (selectedFilters.folders.length > 0) {
    addFilterIfAvailable(
      'folder',
      'isAnyOf',
      selectedFilters.folders.map(folder => denormalizeFolder(folder))
    );
  }

  for (const [fieldName, value] of Object.entries(selectedFilters.metadata)) {
    const columnField = `metadata.${fieldName}`;
    if (isColumnAvailable(columnField)) {
      for (const { operator, filterValue } of metadataFilterValueToOperators(value)) {
        items.push(createFilterItem(id++, columnField, operator, filterValue));
      }
    }
  }

  return { items, logicOperator: GridLogicOperator.And };
}
